import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { lastValueFrom } from 'rxjs';
import { I18nService } from '../../../../../@core/services/i18n/i18n.service';
import { UiLocaleService } from '../../../../../@core/services/i18n/ui-locale.service';
import { ModalService } from '../../../../../@core/services/modal/modal.service';
import { AddressTransportRoute, TransportRoute, TransportRouteRole } from '../../../../../generated/generated-types';
import { TenantService } from '../../../../../shared/services/tenant.service';
import { AddressTransportRouteService } from '../../services/address-transport-route.service';
import { TransportRouteService } from '../../services/transport-route.service';
import { transportRouteIcon } from '../../utils/transport-route-icon.util';

/** One editable row: the saved entry (null for a new row) and the values being edited. */
interface RouteRow {
  key: string;
  saved: AddressTransportRoute | null;
  transportRouteId: string | null;
  transitDays: number | null;
  fixedCost: number | null;
  costPerUnit: number | null;
  notes: string;
}

/**
 * Transport routes of a business partner for one side – as vendor (CREDITOR, inbound, used by the purchase
 * proposal) or as customer (DEBITOR, outbound): route from the catalog, transit days (empty = catalog value),
 * fixed cost per order, cost per unit, one default. Saved per row, straight to the server.
 */
@Component({
  selector: 'app-address-transport-routes-widget',
  templateUrl: './address-transport-routes-widget.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [FormsModule, RouterLink, TranslatePipe, ButtonModule, InputNumberModule, InputTextModule, MessageModule, SelectModule, TableModule, TooltipModule],
})
export class AddressTransportRoutesWidgetComponent {
  private readonly service = inject(AddressTransportRouteService);
  private readonly catalogService = inject(TransportRouteService);
  private readonly modal = inject(ModalService);
  private readonly i18n = inject(I18nService);

  readonly addressId = input.required<string>();
  readonly role = input.required<TransportRouteRole>();
  /** Currency of the partner (creditor or debitor side); costs are entered and shown in it, the tenant's otherwise. */
  readonly currency = input<string | null | undefined>(null);

  private readonly uiLocale = inject(UiLocaleService);
  private readonly tenantService = inject(TenantService);
  private readonly localeVersion = toSignal(this.uiLocale.uiStateChanges$, { initialValue: null });
  /** Numbers are typed in the language of the UI, not of the browser ("2,5" in German stays 2.5). */
  readonly locale = computed(() => {
    this.localeVersion();
    return this.uiLocale.intlLocale;
  });
  readonly currencyCode = computed(() => this.currency() || this.tenantService.currentTenantSig()?.currency || 'EUR');

  readonly catalog = signal<TransportRoute[]>([]);
  readonly rows = signal<RouteRow[]>([]);
  readonly loading = signal<boolean>(false);
  readonly busyKey = signal<string | null>(null);
  readonly error = signal<string | null>(null);

  readonly title = computed(() =>
    this.role() === TransportRouteRole.CREDITOR ? _('transport-route.address.title-creditor') : _('transport-route.address.title-debitor'),
  );
  readonly hint = computed(() =>
    this.role() === TransportRouteRole.CREDITOR ? _('transport-route.address.hint-creditor') : _('transport-route.address.hint-debitor'),
  );
  readonly catalogById = computed(() => new Map(this.catalog().map((route) => [route.id, route])));
  /** Catalog routes not used on this side yet – what "add" and a new row offer. */
  readonly unusedRoutes = computed(() => {
    const used = new Set(this.rows().map((row) => row.transportRouteId));
    return this.catalog().filter((route) => !used.has(route.id));
  });

  private nextKey = 0;

  /** Keeps the row's DOM (and the focused input) while its values change – rows are replaced immutably. */
  readonly trackByKey = (_index: number, row: RouteRow): string => row.key;

  constructor() {
    effect(() => {
      const addressId = this.addressId();
      this.role();
      if (addressId) {
        untracked(() => void this.load());
      }
    });
  }

  async load(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      const [catalog, routes] = await Promise.all([
        this.catalogService.getActiveRoutes(),
        this.service.getRoutes({ addressId: this.addressId(), role: this.role() }),
      ]);
      this.catalog.set(catalog);
      this.rows.set(routes.sort((a, b) => (a.transportRoute?.identifier ?? '').localeCompare(b.transportRoute?.identifier ?? '')).map((route) => this.toRow(route)));
    } catch (error) {
      this.error.set(this.messageOf(error));
    } finally {
      this.loading.set(false);
    }
  }

  addRow(): void {
    const first = this.unusedRoutes()[0];
    this.rows.update((rows) => [
      ...rows,
      { key: `new-${this.nextKey++}`, saved: null, transportRouteId: first?.id ?? null, transitDays: null, fixedCost: null, costPerUnit: null, notes: '' },
    ]);
  }

  /** Options of a row's route select: its own route plus the unused ones. */
  routeOptions(row: RouteRow): TransportRoute[] {
    const own = row.transportRouteId ? this.catalogById().get(row.transportRouteId) : undefined;
    return own ? [own, ...this.unusedRoutes()] : this.unusedRoutes();
  }

  catalogOf(row: RouteRow): TransportRoute | undefined {
    return (row.transportRouteId && this.catalogById().get(row.transportRouteId)) || row.saved?.transportRoute || undefined;
  }

  iconOf(row: RouteRow): string {
    return transportRouteIcon(this.catalogOf(row)?.iconCss);
  }

  routeIcon(route: TransportRoute): string {
    return transportRouteIcon(route.iconCss);
  }

  update(row: RouteRow, patch: Partial<RouteRow>): void {
    this.rows.update((rows) => rows.map((r) => (r.key === row.key ? { ...r, ...patch } : r)));
  }

  isDirty(row: RouteRow): boolean {
    const saved = row.saved;
    if (!saved) {
      return true;
    }
    return (
      row.transportRouteId !== saved.transportRouteId ||
      (row.transitDays ?? null) !== (saved.transitDays ?? null) ||
      (row.fixedCost ?? null) !== (saved.fixedCost ?? null) ||
      (row.costPerUnit ?? null) !== (saved.costPerUnit ?? null) ||
      (row.notes ?? '') !== (saved.notes ?? '')
    );
  }

  async saveRow(row: RouteRow, patch: { isDefault?: boolean } = {}): Promise<void> {
    if (!row.transportRouteId) {
      return;
    }
    this.busyKey.set(row.key);
    this.error.set(null);
    try {
      await this.service.save({
        id: row.saved?.id,
        addressId: this.addressId(),
        role: this.role(),
        transportRouteId: row.transportRouteId,
        transitDays: row.transitDays,
        fixedCost: row.fixedCost,
        costPerUnit: row.costPerUnit,
        notes: row.notes || null,
        // the first route of a side becomes its default
        isDefault: patch.isDefault ?? (row.saved ? row.saved.isDefault : !this.rows().some((r) => r.saved?.isDefault)),
      });
      await this.load();
    } catch (error) {
      this.error.set(this.messageOf(error));
    } finally {
      this.busyKey.set(null);
    }
  }

  revertRow(row: RouteRow): void {
    if (row.saved) {
      this.rows.update((rows) => rows.map((r) => (r.key === row.key ? this.toRow(row.saved!) : r)));
    } else {
      this.rows.update((rows) => rows.filter((r) => r.key !== row.key));
    }
  }

  async setDefault(row: RouteRow): Promise<void> {
    if (row.saved && !row.saved.isDefault) {
      await this.saveRow(row, { isDefault: true });
    }
  }

  async deleteRow(row: RouteRow): Promise<void> {
    if (!row.saved) {
      this.revertRow(row);
      return;
    }
    const confirmed = await lastValueFrom(
      this.modal.showConfirmationDialog({
        title: _('transport-route.address.delete-title'),
        message: _('transport-route.address.delete-message'),
        variables: { route: this.catalogOf(row)?.identifier ?? '' },
        size: 'sm',
      }),
    ).catch(() => false);
    if (!confirmed) {
      return;
    }
    this.busyKey.set(row.key);
    try {
      await this.service.delete(row.saved.id);
      await this.load();
    } catch (error) {
      this.error.set(this.messageOf(error));
    } finally {
      this.busyKey.set(null);
    }
  }

  private toRow(route: AddressTransportRoute): RouteRow {
    return {
      key: route.id,
      saved: route,
      transportRouteId: route.transportRouteId ?? null,
      transitDays: route.transitDays ?? null,
      fixedCost: route.fixedCost ?? null,
      costPerUnit: route.costPerUnit ?? null,
      notes: route.notes ?? '',
    };
  }

  private messageOf(error: unknown): string {
    const message = (error as { message?: string })?.message ?? String(error);
    return this.i18n.translate(_('transport-route.address.save-error'), { message });
  }
}
